/**
 * Phase 50 Plan 07 — Tier summary band.
 *
 * Renders a chip per active SLA tier (`{name}: {taskCount} tasks, {breachCount} breaches`).
 * Mounted above PortfolioTable in ManagerOverviewPage. Backend source:
 * portfolioOverviewService.tierRollup (Plan 50-03).
 *
 * Empty state: returns null when tierRollup is empty (no SLA tasks active).
 * Color: getTierColor(orderIndex) bg + chipTextColor(bg) text for the tier label badge.
 * Breach emphasis: text-rose-600 font-semibold when breachCount > 0; muted when 0.
 */
import type { TierRollupRow } from '../../services/api/portfolio';
import { getTierColor, chipTextColor } from '../../utils/format';

export interface TierSummaryBandProps {
  tierRollup: TierRollupRow[];
}

export function TierSummaryBand({ tierRollup }: TierSummaryBandProps) {
  if (!tierRollup.length) return null;

  const sorted = [...tierRollup].sort((a, b) => a.orderIndex - b.orderIndex);

  return (
    <div
      className="flex flex-wrap items-center gap-3 mb-5"
      role="group"
      aria-label="SLA tier rollup"
    >
      <span className="text-[11.5px] uppercase tracking-wide text-ms-text-muted">
        Tier rollup
      </span>
      {sorted.map((t) => {
        const bg = getTierColor(t.orderIndex);
        const fg = chipTextColor(bg);
        const breachClass =
          t.breachCount > 0 ? 'text-rose-600 font-semibold' : 'text-ms-text-muted';
        return (
          <div
            key={t.tierId}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[11.5px] font-medium border border-ms-border-subtle bg-ms-surface"
            data-testid={`tier-summary-chip-${t.tierId}`}
          >
            <span
              className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold"
              style={{ backgroundColor: bg, color: fg }}
            >
              P{t.orderIndex}
            </span>
            <span className="text-ms-text-strong">{t.name}</span>
            <span className="text-ms-text-secondary">{t.taskCount} tasks</span>
            <span className={breachClass}>{t.breachCount} breaches</span>
          </div>
        );
      })}
    </div>
  );
}
